/**
 * Entertainment Screen Editor
 * 
 * Allows caregivers to edit entertainment screen items (icon, label)
 * with undo support and drag-to-reorder functionality.
 * Supports both flower (cross) and row layouts for editing.
 * Color changes apply to all items as a group.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Pencil, Check, Undo2, RotateCcw, GripVertical, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { BoardItemEditForm } from "@/components/communication/BoardItemEditForm";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";
import { getNavigationColorClasses } from "@/components/communication/NavigationColorPicker";
import { getUserSettings } from "@/lib/userSettings";
import {
  NavigationBoardData,
  NavigationItem,
  loadNavigationBoard,
  saveNavigationBoard,
  updateNavigationItem,
  reorderNavigationItems,
  resetNavigationBoard,
} from "@/lib/navigationBoardTypes";
import { useUnsavedEditGuard } from "@/hooks/useUnsavedEditGuard";

const NAV_STORAGE_KEY = "navigationBoards_v21";

const MAX_UNDO_HISTORY = 20;

type LayoutMode = "row" | "flower";

/**
 * Determine initial layout mode based on user's control settings
 * - Flower: 3+ gestures with gesture-controlled scanning
 * - Row: 1-2 gestures OR automatic scanning
 */
function getInitialLayoutMode(): LayoutMode {
  const settings = getUserSettings();
  const controlMethod = settings.controlMethod;
  const isEyeControl = controlMethod === 'eye_control' || controlMethod === 'eyecontrol';
  const gestureCount = settings.eyeControlGestures?.length || 0;
  const isAutoScan = settings.scanControlMode === 'automatic';
  
  // Row layout for: 1-2 gestures OR automatic scanning
  if (isEyeControl && (gestureCount <= 2 || isAutoScan)) {
    return 'row';
  }
  
  // Flower layout for: 3+ gestures with gesture control OR any non-EyeControl method
  return 'flower';
}

export default function EntertainmentEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const { isRTL: isUserRTL } = useTranslation();
  
  const [board, setBoard] = useState<NavigationBoardData>(() => loadNavigationBoard("entertainment"));
  const [editingItem, setEditingItem] = useState<NavigationItem | null>(null);
  const [undoHistory, setUndoHistory] = useState<NavigationBoardData[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(getInitialLayoutMode);
  const isUndoing = useRef(false);

  const { markSaved } = useUnsavedEditGuard({
    storageKeys: [NAV_STORAGE_KEY],
    onAfterRevert: () => setBoard(loadNavigationBoard("entertainment")),
  });

  // Save to localStorage when board changes
  useEffect(() => {
    if (!isUndoing.current) {
      saveNavigationBoard(board);
    }
  }, [board]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, board];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [board]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setBoard(previousState);
    saveNavigationBoard(previousState);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Get label in user's language for a navigation item
  const getUserLangLabel = (item: NavigationItem): string => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return item.labelAr || item.labelEn;
    if (userLang === "ru") return item.labelRu || item.labelEn;
    if (userLang === "he") return item.labelHe;
    return item.labelEn;
  };

  // Get the update object targeting the user's language field
  const getUserLangUpdate = (name: string): Partial<NavigationItem> => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return { labelAr: name };
    if (userLang === "ru") return { labelRu: name };
    if (userLang === "he") return { labelHe: name };
    return { labelEn: name };
  };

  // Handle save from edit form (including per-item color)
  const handleSaveEdit = (data: { name: string; emoji: string; color: string; showEmoji: boolean; iconName?: string }) => {
    if (!editingItem) return;
    saveToHistory();
    
    const updatedBoard = updateNavigationItem(board, editingItem.id, {
      ...getUserLangUpdate(data.name),
      iconName: data.iconName || editingItem.iconName,
      color: data.color,
    });
    saveNavigationBoard(updatedBoard);
    markSaved();
    window.dispatchEvent(new Event('navigationBoardUpdated'));
    setBoard(updatedBoard);
    setEditingItem(null);
  };

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultBoard = resetNavigationBoard("entertainment");
    setBoard(defaultBoard);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, targetSortedIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetSortedIndex) return;
    
    saveToHistory();
    
    // Swap the order values of dragged and target items
    const draggedItem = sortedItems[draggedIndex];
    const targetItem = sortedItems[targetSortedIndex];
    if (!draggedItem || !targetItem) return;
    
    const updatedItems = board.items.map(item => {
      if (item.id === draggedItem.id) return { ...item, order: targetItem.order };
      if (item.id === targetItem.id) return { ...item, order: draggedItem.order };
      return item;
    });
    
    const updatedBoard: NavigationBoardData = {
      ...board,
      items: updatedItems,
      updatedAt: new Date().toISOString(),
    };
    setBoard(updatedBoard);
    setDraggedIndex(targetSortedIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Handle visibility toggle
  const handleVisibilityToggle = (itemId: string, visible: boolean) => {
    saveToHistory();
    const updatedBoard = updateNavigationItem(board, itemId, { visible });
    setBoard(updatedBoard);
  };

  // If editing, show the edit form
  if (editingItem) {
    return (
      <BoardItemEditForm
        type="navigation"
        initialName={getUserLangLabel(editingItem)}
        initialEmoji=""
        initialColor={editingItem.color as any}
        initialIconName={editingItem.iconName}
        iconMode="lucide"
        iconOnly
        onSave={handleSaveEdit}
        onCancel={() => setEditingItem(null)}
      />
    );
  }

  // Get sorted items
  const sortedItems = [...board.items].sort((a, b) => a.order - b.order);

  // Render a single editable item
  const renderItem = (item: NavigationItem, index: number, className?: string) => {
    const IconComponent = ICON_MAP[item.iconName] || ICON_MAP.Music;
    const userLang = getUserSettings().language || "he";
    const label = userLang === "ar" ? (item.labelAr || item.labelEn) 
                : userLang === "ru" ? (item.labelRu || item.labelEn)
                : userLang === "he" ? item.labelHe 
                : item.labelEn;
    const isVisible = item.visible !== false;
    const colorClasses = getNavigationColorClasses(item.color);
    
    return (
      <motion.div
        key={`${item.id}-${item.color}`}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
        className={cn(
          "relative rounded-2xl border-2 flex flex-col items-center justify-center",
          "transition-all duration-200 cursor-move",
          "bg-card shadow-md",
          draggedIndex === index && "opacity-50 scale-95",
          !isVisible && "opacity-40 grayscale",
          className
        )}
        style={colorClasses.hex ? { borderColor: colorClasses.hex } : undefined}
        whileHover={{ scale: 1.02 }}
      >
        {/* Drag handle */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2">
          <GripVertical className="w-5 h-5 text-muted-foreground" />
        </div>
        
        {/* Edit button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingItem(item);
          }}
          className={cn(
            "absolute top-3 p-2 bg-primary rounded-full hover:bg-primary/80 z-10",
            isRTL ? "right-3" : "left-3"
          )}
        >
          <Pencil className="w-5 h-5 text-primary-foreground" />
        </button>
        
        {/* Visibility toggle button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleVisibilityToggle(item.id, !isVisible);
          }}
          className={cn(
            "absolute top-3 p-2 rounded-full z-10 transition-colors",
            isRTL ? "left-3" : "right-3",
            isVisible 
              ? "bg-accent hover:bg-accent/80" 
              : "bg-muted-foreground/60 hover:bg-muted-foreground/80"
          )}
        >
          {isVisible ? (
            <Eye className="w-5 h-5 text-white" />
          ) : (
            <EyeOff className="w-5 h-5 text-white" />
          )}
        </button>
        
        <IconComponent 
          style={{ width: 'clamp(2.5rem, 10vh, 5rem)', height: 'clamp(2.5rem, 10vh, 5rem)', color: colorClasses.hex || undefined }}
          strokeWidth={2}
        />
        <span 
          dir="auto" 
          className="font-semibold text-card-foreground text-center leading-tight w-full px-2"
          style={{ fontSize: 'clamp(1rem, 2.8vh, 1.5rem)' }}
        >
          {label}
        </span>
      </motion.div>
    );
  };

  // Simple drag-to-swap between the two items (no empty slots)
  // Both row and flower layouts show the same two-card centered view

  const renderLayout = () => {
    const itemSize = 'min(calc((100vw - 6rem) / 2 - 1rem), calc(100vh - 12rem), 75dvh)';
    return (
      <div 
        dir={dir}
        className="flex flex-row items-center justify-center gap-4"
      >
        {sortedItems.map((item, index) => (
          <div key={item.id} style={{ width: itemSize, height: itemSize, flexShrink: 0 }}>
            {renderItem(item, index, "w-full h-full")}
          </div>
        ))}
      </div>
    );
  };

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header with toggle integrated */}
        <div className="flex items-center justify-between px-4 py-3 bg-card/50 border-b border-border">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-foreground">
              {isRTL ? "עריכת מסך הבידור" : "Edit media"}
            </h1>
            
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => { markSaved(); navigate('/board-editor'); }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Items grid */}
        <div className="flex-1 flex items-center justify-center p-2 min-h-0">
          {renderLayout()}
        </div>
      </div>
    </MainLayout>
  );
}
